import React, { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { CookieBanner } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

// Cookies expire in 1 year.

const cookieConsentMaxDuration = 1000 * 60 * 60 * 24 * 365

const ignoredErrors = [
  'Invalid call to runtime.sendMessage(). Tab not found.',
  'WKWebView API client did not respond to this postMessage',
]

const scriptFilePattern = /\.m?js(\?|#|$)/

const isFromInjectedScript = (event: Sentry.ErrorEvent) => {
  const frames =
    event.exception?.values?.flatMap(
      (exception) => exception.stacktrace?.frames ?? []
    ) ?? []

  return (
    frames.length > 0 &&
    frames.every((frame) => !scriptFilePattern.test(frame.filename ?? ''))
  )
}

const TrackingConsentManager: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)
  const { t } = useTranslation('common')

  useEffect(() => {
    const checkConsent = () => {
      let consentData
      try {
        consentData = JSON.parse(
          localStorage.getItem('trackingConsent') || '{}'
        )
      } catch (error) {
        localStorage.removeItem('trackingConsent')
        consentData = {}
      }

      const { trackingConsent, lastConsentDate } = consentData

      let isConsentExpired = true

      if (lastConsentDate) {
        const lastConsentTime = new Date(lastConsentDate).getTime()
        if (!Number.isNaN(lastConsentTime)) {
          isConsentExpired =
            Date.now() - lastConsentTime > cookieConsentMaxDuration
        }
      }

      setShowBanner(isConsentExpired)
      initializeSentry(!isConsentExpired && trackingConsent)
    }

    checkConsent()
  }, [])

  const handleConsent = (consent: boolean) => {
    localStorage.setItem(
      'trackingConsent',
      JSON.stringify({
        trackingConsent: consent,
        lastConsentDate: new Date().toISOString(),
      })
    )
    initializeSentry(consent)
    setShowBanner(false)
  }

  const initializeSentry = (enableTracking: boolean) => {
    Sentry.init({
      dsn: 'https://5e99fba9f03d1c723f06f83b0beb06dd@o731149.ingest.us.sentry.io/4507383872356352',
      tracesSampleRate: 0,
      enabled: enableTracking && import.meta.env.PROD,
      ignoreErrors: ignoredErrors,
      beforeSend: (event) => (isFromInjectedScript(event) ? null : event),
    })
  }

  const handleReadMoreLinkClick = () => {
    window.location.assign(t('cookieConsent.readMoreLink'))
  }

  return (
    <CookieBanner
      show={showBanner}
      onAccept={handleConsent}
      onReadMoreLinkClick={handleReadMoreLinkClick}
      readMoreLink={t('cookieConsent.readMoreLinkText')}
    />
  )
}

export default TrackingConsentManager
